import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { Ecran } from '../../api/contrat.js';
import { creerClient, ErreurApi } from '../../api/client.js';
import { modeMocks } from '../../app/entree.js';
import { MENTION_ESTIMATION } from '../../../../../packages/contracts/src/entities/index.js';
import { creerFetchMocke, sessionDepuisAdresse } from '../../mocks/fetch-mocke.js';
import { CIBLE_MIN, COULEURS, POLICE_EQUIPE } from '../../app/styles.js';
import { FenetreBeta, repondreBeta } from '../../app/beta.js';
import {
  comparer,
  coutPourFamille,
  formaterEuros,
  fourchette,
  instantane,
  niveauFiabilite,
  scenarioPourHypotheses,
  valeurNette,
} from './modele.js';
import { REFERENTIEL } from './referentiel.js';
import { STATUTS_DEVOLUTION } from '../genealogie/libelles.js';
import { chargerEstimation, repondreVisibiliteClient, type DonneesEstimation } from './service.js';
import type { CodeHypothese, EvenementHistorique, Scenario } from './types.js';

/**
 * Conteneur de D04c (lot L62) : estimation des parts, réservée à l'équipe MAAT. Le dossier vient de `?dossier=` ;
 * tant que la coquille ne fournit pas de client aux écrans, l'écran crée le sien (avec `?role=`, parcours de démonstration).
 * La mention indicative est rendue dans tous les états, y compris chargement, erreur et absence de dossier.
 */
const recherche = (): string => globalThis.location?.search ?? '';

function clientDeLEcran() {
  // S1 du 27/10 : `?role=` ne vaut qu'en développement avec les mocks (modeMocks), jamais en production.
  const session = modeMocks(import.meta.env) ? sessionDepuisAdresse(recherche()) : null;
  return creerClient({ fetch: session ? creerFetchMocke({ session }) : globalThis.fetch.bind(globalThis) });
}

const messageDe = (e: unknown): string =>
  e instanceof ErreurApi
    ? e.messageUtilisateur
    : 'Une erreur est survenue. Réessayez dans un instant ; si elle persiste, contactez le Cabinet MAAT.';

const bouton: CSSProperties = {
  minHeight: CIBLE_MIN,
  padding: '0 16px',
  borderRadius: 8,
  border: `1px solid ${COULEURS.marine}`,
  background: COULEURS.blanc,
  color: COULEURS.marine,
  font: 'inherit',
  cursor: 'pointer',
};
const ligneCase: CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', minHeight: CIBLE_MIN };
const carte: CSSProperties = { border: `1px solid ${COULEURS.bordure}`, borderRadius: 8, padding: 16, background: COULEURS.blanc };
const titre2: CSSProperties = { fontSize: 20, color: COULEURS.marine, margin: '0 0 8px' };

function Cadre({ ecran, children }: { ecran: Ecran; children: ReactNode }) {
  return (
    <section
      data-ecran={ecran.id}
      data-lot="L62"
      style={{ fontFamily: POLICE_EQUIPE, display: 'flex', flexDirection: 'column', gap: 16, padding: '0 16px', maxWidth: 960 }}
    >
      <h1 style={{ color: COULEURS.marine, fontSize: 28, margin: 0 }}>{ecran.titre}</h1>
      <p style={{ color: COULEURS.ardoise, margin: 0 }}>Dossier en cours · visible : équipe MAAT</p>
      <p data-mention-estimation style={{ ...carte, background: COULEURS.ambreFond, color: COULEURS.ambre, margin: 0 }}>
        {MENTION_ESTIMATION}
      </p>
      {children}
    </section>
  );
}

const instantTexte = (iso: string): string =>
  new Date(iso).toLocaleString('fr-FR', { timeZone: 'America/Guadeloupe', dateStyle: 'short', timeStyle: 'short' });

function Historique({ evenements }: { evenements: readonly EvenementHistorique[] }) {
  return (
    <div style={carte}>
      <h2 style={titre2}>Historique de la fiche</h2>
      {evenements.length === 0 ? (
        <p style={{ margin: 0 }}>Aucune modification depuis l’ouverture de la fiche.</p>
      ) : (
        <ol style={{ margin: 0, paddingLeft: 20 }}>
          {evenements.map((ev, i) => (
            <li key={`${ev.type}-${ev.instant}-${i}`}>
              {ev.libelle} · {instantTexte(ev.instant)}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

interface PropsBeta {
  question: NonNullable<DonneesEstimation['question']>;
  choix: string;
  erreur: string | null;
  enCours: boolean;
  onChoix: (code: string) => void;
  onEnregistrer: () => void;
  onPlusTard: () => void;
}

function DialogueBeta({ question, choix, erreur, enCours, onChoix, onEnregistrer, onPlusTard }: PropsBeta) {
  return (
    <div role="dialog" aria-labelledby="beta-titre" style={{ ...carte, borderColor: COULEURS.indigo }}>
      <p style={{ margin: 0, color: COULEURS.indigo, fontWeight: 600 }}>BETA test · question à Isabelle</p>
      <h2 id="beta-titre" style={titre2}>
        {question.question}
      </h2>
      <p style={{ color: COULEURS.ardoise }}>Par défaut, l’estimation reste réservée à l’équipe MAAT.</p>
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend style={{ position: 'absolute', left: -9999 }}>{question.question}</legend>
        {question.options.map((o) => (
          <label key={o.code} style={ligneCase}>
            <input type="radio" name="visibilite-client" value={o.code} checked={choix === o.code} onChange={() => onChoix(o.code)} />
            {o.label}
          </label>
        ))}
      </fieldset>
      {erreur ? (
        <p role="alert" style={{ color: COULEURS.rouge }}>
          {erreur}
        </p>
      ) : null}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          style={{ ...bouton, background: COULEURS.boutonPrincipal, color: COULEURS.blanc, borderColor: COULEURS.boutonPrincipal }}
          disabled={enCours}
          onClick={onEnregistrer}
        >
          Enregistrer ma réponse
        </button>
        <button type="button" style={bouton} onClick={onPlusTard}>
          Plus tard
        </button>
      </div>
    </div>
  );
}

/** Le contrat EstimateSchema ne porte aucun statut de validation : le résultat juridique est toujours marqué (SYNTHESE §2). */
const A_VALIDER = STATUTS_DEVOLUTION.a_valider_notaire.mention;

function Resultat({ donnees }: { donnees: DonneesEstimation }) {
  const e = donnees.estimation;
  const niveau = niveauFiabilite(e.reliability.score);
  const net = valeurNette(e);
  const cout = coutPourFamille(e, { tableauVivants: donnees.vivants > 0, arbre: donnees.arbreComplet, estimation: true });
  const euros = (cents: number) => ({ amountCents: cents, currency: 'EUR' as const });
  return (
    <>
      <div style={carte}>
        <h2 style={titre2}>Combien chacun peut espérer aujourd’hui</h2>
        <p style={{ margin: '0 0 8px' }}>
          Frais estimés : {formaterEuros(e.fees)} · Réserve pour les branches inconnues : {formaterEuros(e.reserve)}
        </p>
        <p style={{ margin: '0 0 8px' }}>Reste à partager : {fourchette(euros(net.basse), euros(net.haute))}</p>
        {e.perHeir.length === 0 ? (
          <p>Aucune part n’est calculable pour l’instant. Complétez l’arbre du dossier : l’estimation se recalcule d’elle-même.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th scope="col" style={{ textAlign: 'left' }}>
                    Héritier ou branche
                  </th>
                  <th scope="col" style={{ textAlign: 'left' }}>
                    Part ({A_VALIDER})
                  </th>
                  <th scope="col" style={{ textAlign: 'left' }}>
                    Fourchette
                  </th>
                </tr>
              </thead>
              <tbody>
                {e.perHeir.map((p) => (
                  <tr key={p.personId}>
                    <td>{p.displayName}</td>
                    <td>
                      {p.share.numerator}/{p.share.denominator}
                    </td>
                    <td>{fourchette(p.low, p.high)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ margin: '8px 0 0' }}>Quotes-parts {A_VALIDER} : la notaire ne les a pas encore validées.</p>
          </div>
        )}
      </div>
      <div style={carte}>
        <h2 style={titre2}>Fiabilité : {niveau?.libelle ?? 'non évaluée'}</h2>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {e.reliability.factors.map((f) => (
            <li key={f.label}>
              {f.impact === 'positif' ? 'Point favorable' : f.impact === 'negatif' ? 'Point à surveiller' : 'Point neutre'} : {f.label}
            </li>
          ))}
        </ul>
      </div>
      <div style={carte}>
        <h2 style={titre2}>Coût pour la famille</h2>
        {cout ? (
          <p style={{ margin: 0 }}>
            Coût pour la famille : {formaterEuros(cout.montant)} · {cout.statut}
          </p>
        ) : (
          <p style={{ margin: 0 }}>
            Le coût pour la famille est disponible quand le tableau des vivants, l’arbre et l’estimation sont faits.
          </p>
        )}
      </div>
      <div style={carte}>
        <h2 style={titre2}>Comment l’outil calcule</h2>
        <p style={{ margin: 0 }}>
          Valeur du bien, moins les frais du dossier, multipliée par la part de chacun. La part d’une branche encore inconnue est mise en
          réserve, jamais redistribuée. Le calcul se refait à chaque changement de l’arbre, de la valeur ou des frais.
        </p>
      </div>
    </>
  );
}

export function EcranEstimation({ ecran }: { ecran: Ecran }) {
  const dossierId = useMemo(() => new URLSearchParams(recherche()).get('dossier')?.trim() || null, []);
  const [base, setBase] = useState<Scenario>('vendre');
  const [coches, setCoches] = useState<ReadonlySet<CodeHypothese>>(() => new Set());
  const [relance, setRelance] = useState(0);
  const [donnees, setDonnees] = useState<DonneesEstimation | null>(null);
  const [chargement, setChargement] = useState(dossierId !== null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [journal, setJournal] = useState<readonly EvenementHistorique[]>([]);
  const precedent = useRef<ReturnType<typeof instantane> | null>(null);
  const [choix, setChoix] = useState<string | null>(null);
  const [betaFermee, setBetaFermee] = useState(false);
  const [terrainFermee, setTerrainFermee] = useState(false);
  const [betaErreur, setBetaErreur] = useState<string | null>(null);
  const [betaEnCours, setBetaEnCours] = useState(false);
  const [reponse, setReponse] = useState<string | null>(null);
  const scenario = scenarioPourHypotheses(base, coches);

  useEffect(() => {
    if (dossierId === null) return;
    let annule = false;
    setChargement(true);
    chargerEstimation(clientDeLEcran(), dossierId, scenario)
      .then((d) => {
        if (annule) return;
        const instant = instantane(d.estimation, d.biens);
        const evenements = comparer(precedent.current, instant, new Date().toISOString());
        precedent.current = instant;
        if (evenements.length > 0) setJournal((j) => [...evenements, ...j]);
        setDonnees(d);
        setErreur(null);
        setChargement(false);
      })
      .catch((e: unknown) => {
        if (annule) return;
        setErreur(messageDe(e));
        setChargement(false);
      });
    return () => {
      annule = true;
    };
  }, [dossierId, scenario, relance]);

  if (dossierId === null) {
    return (
      <Cadre ecran={ecran}>
        <p role="status">Aucun dossier n’est ouvert. Ouvrez un dossier depuis le pilotage pour voir son estimation.</p>
      </Cadre>
    );
  }

  const question = donnees?.question ?? null;
  const choixCourant = choix ?? question?.default ?? 'jamais';
  const optionChoisie = question?.options.find((o) => o.code === choixCourant)?.label;

  const enregistrer = async (): Promise<void> => {
    setBetaEnCours(true);
    setBetaErreur(null);
    try {
      await repondreVisibiliteClient(clientDeLEcran(), choixCourant);
      setReponse(optionChoisie ?? choixCourant);
      setBetaFermee(true);
    } catch (e) {
      setBetaErreur(messageDe(e));
    } finally {
      setBetaEnCours(false);
    }
  };

  const basculer = (code: CodeHypothese): void =>
    setCoches((c) => {
      const suivant = new Set(c);
      if (!suivant.delete(code)) suivant.add(code);
      return suivant;
    });
  const libelleScenario = REFERENTIEL.scenarios.find((s) => s.code === scenario)?.libelle ?? scenario;

  return (
    <Cadre ecran={ecran}>
      {chargement && !donnees ? (
        <p role="status" aria-busy="true">
          Chargement de l’estimation…
        </p>
      ) : null}
      {erreur ? (
        <div role="alert" style={{ ...carte, borderColor: COULEURS.rouge }}>
          <p style={{ color: COULEURS.rouge, marginTop: 0 }}>{erreur}</p>
          <button type="button" style={bouton} onClick={() => setRelance((n) => n + 1)}>
            Réessayer
          </button>
        </div>
      ) : null}
      {donnees && !erreur ? (
        <div aria-busy={chargement} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={carte}>
            <h2 style={titre2}>Hypothèses</h2>
            <label style={{ display: 'block' }}>
              Scénario
              <select
                aria-label="Scénario"
                style={{ display: 'block', minHeight: CIBLE_MIN, font: 'inherit', width: '100%', maxWidth: 360 }}
                value={scenario}
                onChange={(ev) => {
                  setCoches(new Set());
                  setBase(ev.target.value as Scenario);
                }}
              >
                {REFERENTIEL.scenarios.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.libelle}
                  </option>
                ))}
              </select>
            </label>
            {REFERENTIEL.hypotheses.map((h) => (
              <label key={h.code} style={ligneCase}>
                <input type="checkbox" checked={coches.has(h.code)} onChange={() => basculer(h.code)} />
                {h.libelle}
              </label>
            ))}
            <p>Scénario : {libelleScenario}</p>
            <button type="button" style={bouton} onClick={() => setRelance((n) => n + 1)}>
              Recalculer
            </button>
          </div>
          <Resultat donnees={donnees} />
          <Historique evenements={journal} />
          <p style={{ margin: 0 }}>
            Visibilité client : réservée à l’équipe MAAT
            {reponse ? ` · réponse enregistrée : ${reponse}` : ' · en attente de la réponse d’Isabelle'}.
          </p>
          {reponse ? <p role="status">Réponse enregistrée.</p> : null}
          {/* BETA-27 (lot 4, point 8 c) : posée après la question de visibilité client, jamais en même temps. */}
          {(!question || betaFermee) && donnees?.questionValeurTerrain && !terrainFermee ? (
            <FenetreBeta
              question={donnees.questionValeurTerrain}
              onRepondre={(option) => {
                const q = donnees.questionValeurTerrain!;
                setTerrainFermee(true);
                void repondreBeta(clientDeLEcran(), q, option, 'D04c').catch(() => undefined);
              }}
              onPlusTard={() => setTerrainFermee(true)}
            />
          ) : null}
          {question && !betaFermee ? (
            <DialogueBeta
              question={question}
              choix={choixCourant}
              erreur={betaErreur}
              enCours={betaEnCours}
              onChoix={setChoix}
              onEnregistrer={() => void enregistrer()}
              onPlusTard={() => setBetaFermee(true)}
            />
          ) : null}
        </div>
      ) : null}
    </Cadre>
  );
}
